import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef, type FormEvent } from "react";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelfieCapture, canCaptureSelfie } from "@/components/profile/selfie-capture";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, CheckCircle, Camera, X } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE_URL = import.meta.env.VITE_API_URL || "";

export const Route = createFileRoute("/signup")({
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession();
    if (session) {
      throw redirect({ to: "/" });
    }
  },
  component: SignupPage,
});

interface Division {
  id: string;
  name: string;
}

interface JobTitle {
  id: string;
  name: string;
}

function SignupPage() {
  return (
    <div className="login-bg min-h-screen flex flex-col items-center justify-center px-6 py-12">
      <img
        src="/paris-safety-logo.png"
        alt="Paris Mechanical"
        className="w-56 md:w-64 mb-3 object-contain"
      />
      <p className="text-text-tertiary text-sm tracking-wider mb-10">
        New Worker Registration
      </p>

      <div className="glass-card rounded-xl shadow-2xl w-full max-w-sm p-8">
        <SignupForm />
      </div>

      <Link to="/login" className="mt-8 text-sm text-text-tertiary hover:text-paris-red-text transition-colors">
        Back to Sign In
      </Link>
    </div>
  );
}

function SignupForm() {
  const navigate = useNavigate();

  // Basic fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Profile fields
  const [jobTitleId, setJobTitleId] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactRelationship, setEmergencyContactRelationship] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");

  // Division picker
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<string[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);

  // Profile photo
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoTempKey, setPhotoTempKey] = useState<string | null>(null);
  const [photoMimeType, setPhotoMimeType] = useState<string | null>(null);
  const [photoSizeBytes, setPhotoSizeBytes] = useState<number | null>(null);
  const [photoFileName, setPhotoFileName] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [selfieOpen, setSelfieOpen] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [divRes, jtRes] = await Promise.all([
          fetch(`${BASE_URL}/api/public/divisions`),
          fetch(`${BASE_URL}/api/public/job-titles`),
        ]);
        if (divRes.ok) {
          const data = await divRes.json() as { data: Division[] };
          setDivisions(data.data || []);
        }
        if (jtRes.ok) {
          const data = await jtRes.json() as { data: JobTitle[] };
          setJobTitles(data.data || []);
        }
      } catch {
        // Non-critical
      }
    })();
  }, []);

  function toggleDivision(id: string) {
    setSelectedDivisionIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  }

  async function handlePhotoSelect(file: File) {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setError("Only JPEG, PNG, or WebP images are accepted for profile photo");
      return;
    }
    if (file.size > 5_000_000) {
      setError("Profile photo must be under 5MB");
      return;
    }
    setError(null);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BASE_URL}/api/public/upload-photo`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json() as { tempKey?: string; mimeType?: string; sizeBytes?: number; fileName?: string; error?: string };
      if (!res.ok || !data.tempKey) {
        throw new Error(data.error || "Photo upload failed");
      }
      setPhotoTempKey(data.tempKey);
      setPhotoMimeType(data.mimeType ?? null);
      setPhotoSizeBytes(data.sizeBytes ?? null);
      setPhotoFileName(data.fileName ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Photo upload failed");
      setPhotoPreview(null);
      setPhotoTempKey(null);
    } finally {
      setPhotoUploading(false);
    }
  }

  function clearPhoto() {
    setPhotoPreview(null);
    setPhotoTempKey(null);
    setPhotoMimeType(null);
    setPhotoSizeBytes(null);
    setPhotoFileName(null);
    if (photoInputRef.current) photoInputRef.current.value = "";
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) return setError("Full name is required");
    if (!email.trim()) return setError("Email is required");
    if (!phone.trim()) return setError("Phone number is required");
    if (!contactEmail.trim()) return setError("Contact email is required");
    if (!jobTitleId) return setError("Job title is required");
    if (!birthDate) return setError("Date of birth is required");
    if (!emergencyContactName.trim()) return setError("Emergency contact name is required");
    if (!emergencyContactRelationship.trim()) return setError("Emergency contact relationship is required");
    if (!emergencyContactPhone.trim()) return setError("Emergency contact phone is required");
    if (selectedDivisionIds.length === 0) return setError("Select at least one division");
    if (!photoTempKey) return setError("Profile photo is required");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (password !== confirmPassword) return setError("Passwords don't match");

    setIsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          password,
          divisionIds: selectedDivisionIds,
          jobTitleId: jobTitleId || undefined,
          birthDate: birthDate || undefined,
          contactEmail: contactEmail.trim() || undefined,
          emergencyContactName: emergencyContactName.trim(),
          emergencyContactRelationship: emergencyContactRelationship.trim(),
          emergencyContactPhone: emergencyContactPhone.trim(),
          ...(photoTempKey ? {
            signupPhotoTempKey: photoTempKey,
            signupPhotoMimeType: photoMimeType,
            signupPhotoSizeBytes: photoSizeBytes,
            signupPhotoFileName: photoFileName,
          } : {}),
        }),
      });

      const data = await res.json() as { success?: boolean; error?: string };

      if (!res.ok) {
        setError(data.error || "Failed to create account");
        return;
      }

      setSuccess(true);
    } catch {
      setError("Network error. Try again.");
    } finally {
      setIsLoading(false);
    }
  }

  if (success) {
    return (
      <div className="text-center space-y-4 py-6">
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="h-7 w-7 text-green-500" />
          </div>
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Account Created</h2>
        <p className="text-sm text-text-secondary">
          Your registration is pending approval from an administrator.
          You'll be able to sign in once approved.
        </p>
        <Button
          onClick={() => navigate({ to: "/login" })}
          className="w-full mt-4"
        >
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-text-primary">Create Account</h2>
        <p className="text-sm text-text-secondary mt-1">
          New worker registration — admin approval required
        </p>
      </div>

      {/* ── Personal info ─── */}
      <div className="space-y-1.5">
        <Label htmlFor="name" className="text-xs uppercase tracking-wider text-text-secondary">
          Full Name <span className="text-red-400 normal-case">*</span>
        </Label>
        <Input
          id="name"
          type="text"
          placeholder="John Smith"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          autoFocus
          disabled={isLoading}
          className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email" className="text-xs uppercase tracking-wider text-text-secondary">
          Login Email <span className="text-red-400 normal-case">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="you@parismechanical.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          disabled={isLoading}
          className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-text-secondary">
          Phone <span className="text-red-400 normal-case">*</span>
        </Label>
        <Input
          id="phone"
          type="tel"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          disabled={isLoading}
          className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-email" className="text-xs uppercase tracking-wider text-text-secondary">
          Contact Email <span className="text-red-400 normal-case">*</span>
        </Label>
        <Input
          id="contact-email"
          type="email"
          placeholder="Personal email for contact"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          autoComplete="email"
          disabled={isLoading}
          className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="birth-date" className="text-xs uppercase tracking-wider text-text-secondary">
          Date of Birth <span className="text-red-400 normal-case">*</span>
        </Label>
        <Input
          id="birth-date"
          type="date"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
          max={new Date().toISOString().split("T")[0]}
          min="1900-01-01"
          disabled={isLoading}
          className="min-h-11 bg-surface-base/50 border-border-default text-text-primary"
        />
      </div>

      {/* ── Job info ─── */}
      {jobTitles.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-text-secondary">
            Job Title <span className="text-red-400 normal-case">*</span>
          </Label>
          <select
            value={jobTitleId}
            onChange={(e) => setJobTitleId(e.target.value)}
            disabled={isLoading}
            className="w-full min-h-11 px-3 rounded-md border border-border-default bg-surface-base/50 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">— Select your job title —</option>
            {[...jobTitles].sort((a, b) => a.name.localeCompare(b.name)).map((jt) => (
              <option key={jt.id} value={jt.id}>{jt.name}</option>
            ))}
          </select>
        </div>
      )}

      {divisions.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-text-secondary">
            Division(s) <span className="text-red-400 normal-case">*</span>
            <span className="text-text-muted normal-case ml-1">(select all that apply)</span>
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {divisions.map((d) => {
              const isSelected = selectedDivisionIds.includes(d.id);
              const isPrimary = selectedDivisionIds[0] === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDivision(d.id)}
                  disabled={isLoading}
                  className={cn(
                    "px-2.5 py-1.5 rounded-full text-xs border transition-colors",
                    isSelected
                      ? isPrimary
                        ? "bg-paris-red text-white border-paris-red"
                        : "bg-paris-red/15 text-paris-red-text border-paris-red/40"
                      : "bg-surface-base/50 text-text-secondary border-border-default hover:border-text-muted"
                  )}
                >
                  {d.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Emergency contact ─── */}
      <div className="pt-2 border-t border-border-default">
        <p className="text-xs uppercase tracking-wider text-text-secondary mb-3">
          Emergency Contact <span className="text-red-400 normal-case">*</span>
        </p>
        <div className="space-y-3">
          <Input
            type="text"
            placeholder="Full name"
            value={emergencyContactName}
            onChange={(e) => setEmergencyContactName(e.target.value)}
            disabled={isLoading}
            className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
          />
          <Input
            type="text"
            placeholder="Relationship (e.g. Spouse, Parent, Sibling)"
            value={emergencyContactRelationship}
            onChange={(e) => setEmergencyContactRelationship(e.target.value)}
            disabled={isLoading}
            className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
          />
          <Input
            type="tel"
            placeholder="Phone number"
            value={emergencyContactPhone}
            onChange={(e) => setEmergencyContactPhone(e.target.value)}
            disabled={isLoading}
            className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
          />
        </div>
      </div>

      {/* ── Profile photo (optional) ─── */}
      <div className="pt-2 border-t border-border-default">
        <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">
          Profile Photo <span className="text-red-400 normal-case">*</span>
        </p>
        <div className="flex items-center gap-3">
          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Profile preview"
                className="h-16 w-16 rounded-full object-cover border border-border-default"
              />
              {photoUploading && (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                </div>
              )}
              {!photoUploading && (
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={isLoading || photoUploading}
              className="h-16 w-16 rounded-full border-2 border-dashed border-border-default bg-surface-base/50 flex items-center justify-center text-text-muted hover:border-text-muted transition-colors"
            >
              <Camera className="h-6 w-6" />
            </button>
          )}
          <div className="flex-1">
            {!photoPreview ? (
              <div className="flex items-center gap-3">
                {canCaptureSelfie() && (
                  <button
                    type="button"
                    onClick={() => setSelfieOpen(true)}
                    disabled={isLoading || photoUploading}
                    className="text-sm text-paris-red-text hover:underline"
                  >
                    Take a selfie
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isLoading || photoUploading}
                  className="text-sm text-paris-red-text hover:underline"
                >
                  Upload photo
                </button>
              </div>
            ) : photoTempKey ? (
              <p className="text-xs text-green-600">Photo uploaded</p>
            ) : photoUploading ? (
              <p className="text-xs text-text-muted">Uploading...</p>
            ) : null}
            <p className="text-xs text-text-muted mt-0.5">JPEG, PNG or WebP · max 5MB</p>
          </div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePhotoSelect(file);
            }}
          />
        </div>
        <SelfieCapture
          open={selfieOpen}
          onClose={() => setSelfieOpen(false)}
          onCapture={handlePhotoSelect}
          onChooseFile={() => photoInputRef.current?.click()}
        />
      </div>

      {/* ── Password ─── */}
      <div className="pt-2 border-t border-border-default space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-xs uppercase tracking-wider text-text-secondary">
            Password <span className="text-red-400 normal-case">*</span>
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={isLoading}
              className="min-h-11 pr-10 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 rounded text-text-tertiary hover:text-text-secondary"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wider text-text-secondary">
            Confirm Password <span className="text-red-400 normal-case">*</span>
          </Label>
          <Input
            id="confirm-password"
            type={showPassword ? "text" : "password"}
            placeholder="Re-enter password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={isLoading}
            className="min-h-11 bg-surface-base/50 border-border-default text-text-primary placeholder:text-text-muted"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-error/10 border border-error/20 px-4 py-3">
          <p className="text-sm text-paris-red-text">{error}</p>
        </div>
      )}

      <Button
        type="submit"
        className="w-full min-h-12 text-base bg-paris-red hover:bg-paris-red-hover text-white glow-red"
        disabled={isLoading}
        size="lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="animate-spin" />
            Creating account...
          </>
        ) : (
          "Create Account"
        )}
      </Button>
    </form>
  );
}
